import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Play, Pause, Music2 } from "lucide-react";

const getYouTubeId = (url) => {
  if (!url) return null;
  const m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/);
  return m ? m[1] : null;
};

export const SongPlayer = () => {
  const [song, setSong] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [progress, setProgress] = useState(0);
  const audioRef = useRef(null);
  const ytFrameRef = useRef(null);

  useEffect(() => {
    api.get("/song-of-the-day").then((r) => setSong(r.data)).catch(() => {});
  }, []);

  const ytId = getYouTubeId(song?.audio_url);
  const isYoutube = !!ytId;

  useEffect(() => {
    if (isYoutube) return;
    const a = audioRef.current;
    if (!a) return;
    const onTime = () => {
      if (a.duration) setProgress((a.currentTime / a.duration) * 100);
    };
    const onEnd = () => setPlaying(false);
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("ended", onEnd);
    return () => {
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("ended", onEnd);
    };
  }, [song, isYoutube]);

  const toggleMp3 = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) {
      a.pause();
      setPlaying(false);
    } else {
      a.play().then(() => setPlaying(true)).catch(() => {});
    }
  };

  const toggleYouTube = () => {
    const f = ytFrameRef.current;
    if (!f) return;
    const cmd = playing ? "pauseVideo" : "playVideo";
    f.contentWindow.postMessage(
      JSON.stringify({ event: "command", func: cmd, args: [] }),
      "*"
    );
    setPlaying(!playing);
  };

  if (!song?.audio_url) return null;

  return (
    <div
      data-testid="song-player"
      className={`fixed right-6 bottom-6 z-40 transition-all duration-500 ${
        collapsed ? "w-14" : "w-72"
      }`}
    >
      {isYoutube ? (
        <iframe
          ref={ytFrameRef}
          src={`https://www.youtube.com/embed/${ytId}?enablejsapi=1&controls=0&modestbranding=1&playsinline=1`}
          allow="autoplay; encrypted-media"
          title="song"
          className="absolute opacity-0 pointer-events-none"
          width="1"
          height="1"
        />
      ) : (
        <audio ref={audioRef} src={song.audio_url} preload="metadata" />
      )}

      {collapsed ? (
        <button
          data-testid="song-expand"
          onClick={() => setCollapsed(false)}
          className="h-14 w-14 bg-[#0e0e0e] border border-[var(--gold)]/30 flex items-center justify-center text-[var(--gold)] hover:border-[var(--gold)] transition-colors"
        >
          <Music2 size={18} />
        </button>
      ) : (
        <div className="bg-[#0e0e0e]/95 backdrop-blur-md border border-[var(--gold)]/25 p-4 shadow-2xl">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Music2 size={12} className="text-[var(--gold)]" />
              <span className="label-caps text-[0.6rem]">Song of the Day</span>
            </div>
            <button
              data-testid="song-collapse"
              onClick={() => setCollapsed(true)}
              className="text-gray-500 hover:text-white text-xs px-2"
              title="Hide"
            >
              ✕
            </button>
          </div>

          <div className="flex items-center gap-3">
            <button
              data-testid="song-toggle"
              onClick={isYoutube ? toggleYouTube : toggleMp3}
              className="h-12 w-12 shrink-0 bg-[var(--gold)] hover:bg-[var(--gold-bright)] text-black flex items-center justify-center transition-colors"
              title={playing ? "Παύση" : "Αναπαραγωγή"}
            >
              {playing ? <Pause size={18} /> : <Play size={18} className="ml-0.5" />}
            </button>
            <div className="min-w-0 flex-1">
              <div className="font-display text-sm font-semibold truncate">{song.title}</div>
              {song.artist && (
                <div className="text-[0.65rem] text-gray-400 tracking-widest uppercase truncate">
                  {song.artist}
                </div>
              )}
            </div>
          </div>

          {/* Progress bar (MP3 only) */}
          {!isYoutube && (
            <div className="mt-3 h-0.5 bg-white/10 overflow-hidden">
              <div
                className="h-full bg-[var(--gold)] transition-[width] duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};
